/**
 * Thin fetch wrapper for the imgwala Lab API.
 *
 * - Base: `/api` (same-origin. Dev = Vite proxy to lab.imgwala.com. Prod = same domain via CF Pages + worker route.)
 * - Credentials: always `include` so cookies (auth_token) flow.
 * - Errors: HTTP 4xx/5xx throw `ApiError`. Successful JSON returns typed T.
 * - Body: JSON object → auto-serialized. FormData / Blob passed through untouched.
 */

export type ApiErrorShape = {
  error?: string;
  message?: string;
  details?: unknown;
};

export class ApiError extends Error {
  status: number;
  code?: string;
  data?: ApiErrorShape;

  constructor(status: number, message: string, data?: ApiErrorShape) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.data = data;
    this.code = data?.error;
  }
}

export type ApiRequestOpts = {
  signal?: AbortSignal;
  headers?: Record<string, string>;
  /** Skip Content-Type: application/json even if body is a plain object. */
  raw?: boolean;
};

const BASE = "/api";

function buildUrl(path: string): string {
  if (/^https?:\/\//i.test(path)) return path;
  const clean = path.startsWith("/") ? path : `/${path}`;
  return `${BASE}${clean}`;
}

async function request<T>(
  method: string,
  path: string,
  body?: unknown,
  opts: ApiRequestOpts = {}
): Promise<T> {
  const headers: Record<string, string> = { Accept: "application/json", ...opts.headers };
  let payload: BodyInit | undefined;

  if (body instanceof FormData || body instanceof Blob || body instanceof ArrayBuffer) {
    payload = body as BodyInit;
  } else if (body !== undefined && body !== null) {
    if (!opts.raw) headers["Content-Type"] = "application/json";
    payload = JSON.stringify(body);
  }

  let res: Response;
  try {
    res = await fetch(buildUrl(path), {
      method,
      credentials: "include",
      headers,
      body: payload,
      signal: opts.signal,
    });
  } catch (err) {
    // Network / CORS / aborted
    if ((err as { name?: string })?.name === "AbortError") throw err;
    throw new ApiError(0, "Network error. Check your connection.", {
      error: "network_error",
      message: (err as Error)?.message,
    });
  }

  const contentType = res.headers.get("content-type") || "";
  const isJson = contentType.includes("application/json");

  if (!res.ok) {
    let data: ApiErrorShape | undefined;
    let message = res.statusText || `HTTP ${res.status}`;
    if (isJson) {
      try {
        data = (await res.json()) as ApiErrorShape;
        message = data?.message || data?.error || message;
      } catch {
        /* body not JSON despite header — ignore */
      }
    } else {
      try {
        const text = await res.text();
        if (text) message = text.slice(0, 300);
      } catch {
        /* ignore */
      }
    }
    throw new ApiError(res.status, message, data);
  }

  if (res.status === 204) return undefined as T;
  if (isJson) return (await res.json()) as T;
  return (await res.text()) as unknown as T;
}

export const api = {
  get: <T>(path: string, opts?: ApiRequestOpts) => request<T>("GET", path, undefined, opts),
  post: <T>(path: string, body?: unknown, opts?: ApiRequestOpts) =>
    request<T>("POST", path, body, opts),
  put: <T>(path: string, body?: unknown, opts?: ApiRequestOpts) =>
    request<T>("PUT", path, body, opts),
  patch: <T>(path: string, body?: unknown, opts?: ApiRequestOpts) =>
    request<T>("PATCH", path, body, opts),
  delete: <T>(path: string, opts?: ApiRequestOpts) =>
    request<T>("DELETE", path, undefined, opts),
};

/** Type guard for use in mutation onError handlers. */
export function isApiError(err: unknown): err is ApiError {
  return err instanceof ApiError;
}
